import { z } from 'zod'
import { TASK_PRIORITIES, TASK_STATUSES } from '../enums'
import { csvEnum, optionalDate, optionalId, optionalText, requiredText } from './common'

/**
 * The three association fields are independent and all optional — a task with none
 * of them is a general task (spec §4.1).
 */
const taskFields = z.object({
  name: requiredText(300, 'שם המשימה הוא שדה חובה'),
  description: optionalText(4000),
  assignedUserId: optionalId(),
  startDate: optionalDate(),
  dueDate: optionalDate(),
  priority: z.enum(TASK_PRIORITIES).default('MEDIUM'),
  status: z.enum(TASK_STATUSES).default('TODO'),
  clientId: optionalId(),
  caseId: optionalId(),
  projectId: optionalId(),
  parentTaskId: optionalId(),
})

export const createTaskSchema = taskFields

/** Inline grid editing sends single fields, so updates are genuinely partial. */
export const updateTaskSchema = taskFields.partial()

export type CreateTaskInput = z.infer<typeof createTaskSchema>
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>

export const TASK_SORT_FIELDS = ['dueDate', 'startDate', 'priority', 'status', 'name', 'createdAt'] as const
export type TaskSortField = (typeof TASK_SORT_FIELDS)[number]

export const taskQuerySchema = z.object({
  search: optionalText(200),
  assignedUserId: optionalId(),
  clientId: optionalId(),
  caseId: optionalId(),
  projectId: optionalId(),
  parentTaskId: optionalId(),
  status: csvEnum(TASK_STATUSES),
  priority: csvEnum(TASK_PRIORITIES),
  dueFrom: optionalDate(),
  dueTo: optionalDate(),
  /** Only tasks with no parent — used for the grouped tree view. */
  topLevelOnly: z.preprocess(
    (v) => (v === 'true' || v === true ? true : v === 'false' || v === false ? false : undefined),
    z.boolean().optional()
  ),
  sortBy: z.enum(TASK_SORT_FIELDS).default('dueDate'),
  sortDir: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(100),
})

export type TaskQuery = z.infer<typeof taskQuerySchema>

export type TaskDto = {
  id: string
  name: string
  description: string | null
  assignedUserId: string | null
  assignedUserName: string | null
  startDate: string | null
  dueDate: string | null
  priority: (typeof TASK_PRIORITIES)[number]
  status: (typeof TASK_STATUSES)[number]
  clientId: string | null
  clientName: string | null
  caseId: string | null
  caseName: string | null
  projectId: string | null
  projectName: string | null
  parentTaskId: string | null
  parentTaskName: string | null
  subtaskCount: number
  templateId: string | null
  createdById: string
  createdByName: string | null
  completedAt: string | null
  createdAt: string
  updatedAt: string
}
